import { useMemo, useState } from "react";
import { useBooking } from "./hooks/useBooking";

import { STAFF_THEME, FONT } from "../../theme/staff";
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

const pillStyle = {
  background: BRAND.panel,
  color: BRAND.cream,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "999px",
  padding: "0.4rem 0.85rem",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.85rem",
};

function tsToDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v instanceof Date) return v;
  return new Date(v);
}

function fmtDate(v) {
  const d = tsToDate(v);
  if (!d || isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function fmtShortDate(v) {
  const d = tsToDate(v);
  if (!d || isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtTime(v) {
  const d = tsToDate(v);
  if (!d || isNaN(d)) return null;
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function fmtMoney(n) {
  if (n == null || n === "") return null;
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (!Number.isFinite(num)) return null;
  return "$" + num.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: num % 1 === 0 ? 0 : 2 });
}

function hoursBetween(a, b) {
  const da = tsToDate(a), db = tsToDate(b);
  if (!da || !db || isNaN(da) || isNaN(db)) return null;
  const hrs = (db - da) / (60 * 60 * 1000);
  if (hrs <= 0) return null;
  return Math.round(hrs * 10) / 10;
}

// ---------- block generators ----------

function genContractName(b) {
  const who = b.company || b.name || "Client";
  const evt = b.eventName || "Event";
  const date = fmtShortDate(b.confirmedDate) || fmtShortDate(b.inquiryEventDate) || "TBD";
  return `Joy of Pancakes — ${who} — ${evt} — ${date}`;
}

function genDescription(b) {
  const parts = [];
  const who = b.company || b.name;
  const dateStr = fmtDate(b.confirmedDate) || fmtDate(b.inquiryEventDate);
  const venue = [b.venueName, b.location].filter(Boolean).join(", ");

  let opener = "The Joy of Pancakes";
  if (who) opener += ` will be performing for ${who}`;
  if (b.eventName) opener += ` at ${b.eventName}`;
  if (dateStr) opener += ` on ${dateStr}`;
  if (venue) opener += ` at ${venue}`;
  opener += ".";
  parts.push(opener);

  const details = [];
  if (b.attendance) details.push(`${b.attendance} expected guests`);
  if (b.indoorOutdoor) details.push(b.indoorOutdoor.toLowerCase());
  if (b.performanceFormat) details.push(b.performanceFormat);
  if (details.length > 0) parts.push(`Event details: ${details.join(", ")}.`);

  if (b.guestExperience) parts.push(`Guest experience: ${b.guestExperience}`);

  return parts.join(" ");
}

function genScope(b) {
  const bullets = [];

  // Service lines by event type
  const et = b.eventType;
  if (et === "standard" || et === null || et === undefined || et === "") {
    bullets.push("Live pancake art performance — custom-drawn pancakes served to guests");
  } else if (et === "plus_poffertjes") {
    bullets.push("Live pancake art performance — custom-drawn pancakes served to guests");
    bullets.push("Poffertjes service (Dutch mini-pancakes) with assorted toppings");
  } else if (et === "plus_canopy") {
    bullets.push("Live pancake art performance — custom-drawn pancakes served to guests");
    bullets.push("Branded Joy of Pancakes canopy setup");
  } else if (et === "poffertjes_only") {
    bullets.push("Poffertjes service (Dutch mini-pancakes) with assorted toppings");
  } else if (et === "other") {
    if (b.services) bullets.push(b.services);
    else bullets.push("Custom pancake service — see description");
  }

  // Timing
  const start = fmtTime(b.confirmedStart);
  const end = fmtTime(b.confirmedEnd);
  const hrs = hoursBetween(b.confirmedStart, b.confirmedEnd);
  if (start && end) bullets.push(`Service window: ${start} – ${end}${hrs ? ` (${hrs} hours)` : ""}`);
  else if (hrs) bullets.push(`Service window: ${hrs} hours`);

  if (b.attendance) bullets.push(`Expected attendance: ${b.attendance} guests`);

  // Extras
  if (b.flashBoard) {
    bullets.push("Flash board: a curated menu of pre-designed pancake art for guests to choose from");
    if (b.flashBoardNotes) bullets.push(`Flash board notes: ${b.flashBoardNotes}`);
  }
  if (b.tvNeeded) bullets.push("Client to provide a TV / monitor for overhead-camera display");
  if (b.artFormat) bullets.push(`Art format: ${b.artFormat}`);
  if (b.outputTarget) bullets.push(`Output: ${b.outputTarget}`);
  if (b.fancyFlavor) bullets.push(`Specialty flavor: ${b.fancyFlavor}`);

  // Venue logistics
  if (b.venueAddress) bullets.push(`Venue: ${b.venueAddress}`);
  if (b.dayOfContactName) {
    const contact = [b.dayOfContactName, b.dayOfContactPhone].filter(Boolean).join(" · ");
    bullets.push(`Day-of contact: ${contact}`);
  }

  return bullets.map((b) => `• ${b}`).join("\n");
}

function genCompensation(b) {
  const lines = [];
  const total = fmtMoney(b.totalRate);
  if (total) {
    lines.push(`Total rate: ${total}`);
    const rate = parseFloat(b.totalRate);
    if (Number.isFinite(rate) && rate > 0) {
      const deposit = fmtMoney(rate * 0.5);
      const balance = fmtMoney(rate - rate * 0.5);
      lines.push(`  · 50% deposit due upon booking: ${deposit}`);
      lines.push(`  · Balance due on day of event: ${balance}`);
    }
  } else if (b.quotedAmount) {
    lines.push(`Quoted amount: ${fmtMoney(b.quotedAmount)}`);
  } else {
    lines.push("Total rate: TBD");
  }

  if (b.overtimeRate) {
    lines.push(`Overtime rate: ${fmtMoney(b.overtimeRate)} per hour beyond scheduled service window`);
  }

  if (b.departureFrom === "travel" || b.flightInfo || b.hotelInfo || b.rentalCar || b.travelExpenses) {
    lines.push("");
    lines.push("Travel & accommodations (billed separately or reimbursed):");
    if (b.flightInfo)      lines.push(`  · Flight: ${b.flightInfo}`);
    if (b.hotelInfo)       lines.push(`  · Hotel: ${b.hotelInfo}`);
    if (b.rentalCar)       lines.push(`  · Ground transport: ${b.rentalCar}`);
    if (b.travelExpenses)  lines.push(`  · Other travel expenses: ${b.travelExpenses}`);
  }

  if (b.isPublicEvent) {
    lines.push("");
    lines.push("Public event — ticketing / promotion terms apply as separately agreed.");
  }

  return lines.join("\n");
}

function genAll(b) {
  return [
    `CONTRACT NAME\n${genContractName(b)}`,
    `\nDESCRIPTION\n${genDescription(b)}`,
    `\nSCOPE OF WORK\n${genScope(b)}`,
    `\nCOMPENSATION\n${genCompensation(b)}`,
  ].join("\n");
}

// ---------- UI ----------

function CopyBlock({ title, content }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback — select text in a textarea
      const ta = document.createElement("textarea");
      ta.value = content;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      padding: "0.9rem",
      marginBottom: "0.85rem",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "0.6rem",
      }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.04em" }}>
          {title.toUpperCase()}
        </div>
        <button
          type="button"
          onClick={onCopy}
          style={{
            background: copied ? BRAND.green : BRAND.blue,
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "0.35rem 0.8rem",
            fontWeight: 700,
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          {copied ? "✓ Copied" : "📋 Copy"}
        </button>
      </div>
      <pre style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "inherit",
        margin: 0,
        color: BRAND.cream,
        fontSize: "0.9rem",
        lineHeight: 1.5,
      }}>
        {content || "(nothing to generate — fill in more booking fields)"}
      </pre>
    </div>
  );
}

export default function ContractHelper({ id, navigate }) {
  const { booking, loading, error } = useBooking(id);
  const blocks = useMemo(() => {
    if (!booking) return null;
    return {
      name: genContractName(booking),
      description: genDescription(booking),
      scope: genScope(booking),
      compensation: genCompensation(booking),
      all: genAll(booking),
    };
  }, [booking]);

  if (loading) {
    return (
      <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading booking…
      </div>
    );
  }
  if (error || !booking) {
    return (
      <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.text, padding: "2rem" }}>
        <button onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
        <div style={{ marginTop: "1rem", color: BRAND.pink }}>{error ? error.message : "Not found"}</div>
      </div>
    );
  }

  return (
    <div style={{
      background: BRAND.bg,
      minHeight: "100vh",
      color: BRAND.text,
      fontFamily: FONT.body,
      padding: "1rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: "1.25rem", color: BRAND.gold, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            📜 Contract Helper
          </h1>
          <button type="button" onClick={() => navigate(`/crm/bookings/${id}`)} style={pillStyle}>← Booking</button>
        </div>
        <p style={{ color: BRAND.muted, fontSize: "0.85rem", marginTop: 0, marginBottom: "1rem" }}>
          Copy each block into DocuSign / Google Docs / whatever you draft contracts in. Edit on the booking detail screen to refine.
        </p>

        <CopyBlock title="Contract name" content={blocks.name} />
        <CopyBlock title="Description" content={blocks.description} />
        <CopyBlock title="Scope of work" content={blocks.scope} />
        <CopyBlock title="Compensation" content={blocks.compensation} />

        <div style={{ borderTop: `1px solid ${BRAND.line}`, paddingTop: "1rem", marginTop: "0.5rem" }}>
          <CopyBlock title="Everything at once" content={blocks.all} />
        </div>
      </div>
    </div>
  );
}
